import { Box } from '@mui/material';
import React, { useId } from 'react';

import { ContactProps } from './Contact.props';
import { RoleBox } from './Contact.styles';
import { RESPONSIVE_FIELD_GRID_SX } from '../../../configs/ui-constants';
import { SelectMenu } from '../../atoms/SelectMenu';
import { TextField } from '../../atoms/TextField';
import { EmailTextField } from '../../molecules/EmailTextField';

/**
 * The six contact fields, in as many columns as the container can hold.
 *
 * They were two rows of `xs: 4` — a hard three columns at *every* width, so the
 * form neither collapsed on a phone nor widened on a page. Both rows now share
 * one grid, which also lets the fields reflow rather than leaving a ragged gap
 * when the contact-type selector is hidden.
 */
export const Contact: React.FC<ContactProps> = ({
  contactType,
  onChange,
  error,
  contactData,
  renderNotes = true,
  disableTypeSelection = false,
  hideContactType,
  hasContactEditPermission = true,
}) => {
  const contactTypeMenuId = useId();

  return (
    <RoleBox>
      <Box sx={{ containerType: 'inline-size' }}>
        <Box sx={RESPONSIVE_FIELD_GRID_SX}>
          {!hideContactType && (
            <Box>
              <SelectMenu
                // Unique per instance: the structure detail page renders Contact
                // more than once, and a duplicated id breaks the label-to-control
                // association assistive technology relies on. (It also used to
                // claim to be a page-size menu.)
                id={contactTypeMenuId}
                minWidth="100%"
                items={contactType || []}
                selectedValue={contactData.contactType}
                disabled={disableTypeSelection}
                onChange={(text) =>
                  onChange({
                    ...contactData,
                    contactType: text,
                  })
                }
              />
            </Box>
          )}
          <Box>
            <TextField
              fullWidth
              disabled={!hasContactEditPermission}
              error={error}
              label="Name"
              value={contactData.contactName}
              onChange={(text) =>
                onChange({
                  ...contactData,
                  contactName: text,
                })
              }
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              disabled={!hasContactEditPermission}
              error={error}
              label="Organisation"
              value={contactData?.organization}
              onChange={(text) =>
                onChange({
                  ...contactData,
                  organization: text,
                })
              }
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              disabled={!hasContactEditPermission}
              error={error}
              label="Phone 1"
              value={contactData?.phone1}
              onChange={(text) =>
                onChange({
                  ...contactData,
                  phone1: text,
                })
              }
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              disabled={!hasContactEditPermission}
              error={error}
              label="Phone 2"
              value={contactData?.phone2}
              onChange={(text) =>
                onChange({
                  ...contactData,
                  phone2: text,
                })
              }
            />
          </Box>
          <Box>
            <EmailTextField
              fullWidth
              disabled={!hasContactEditPermission}
              value={contactData?.email}
              onChange={(text) =>
                onChange({
                  ...contactData,
                  email: text,
                })
              }
            />
          </Box>
        </Box>

        {renderNotes && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              disabled={!hasContactEditPermission}
              label="Notes"
              multiline
              rows={4}
              value={contactData?.notes}
              onChange={(text) =>
                onChange({
                  ...contactData,
                  notes: text,
                })
              }
            />
          </Box>
        )}
      </Box>
    </RoleBox>
  );
};
